"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { FileSearch, Wrench, MapPin, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const stepsData = [{
  number: "01",
  title: "Technical & Strategic SEO Audit",
  shortTitle: "Audit",
  description: "I perform a granular, hands-on audit of your site architecture, technical health, crawl efficiency, and content gaps to uncover immediate low-hanging fruit and sustainable growth avenues.",
  deliverables: "In-depth Technical Audit, Action Plan & Keyword Mapping",
  iconKey: "audit"
}, {
  number: "02",
  title: "On-Page & Search Intent Alignment",
  shortTitle: "Optimization",
  description: "I optimize existing content and build strategic new landing pages optimized for buyer intent, semantic relevance, user experience, and conversion efficiency.",
  deliverables: "Intent-Matched Copy, On-Page Tweaks & Metadata Optimization",
  iconKey: "optimization"
}, {
  number: "03",
  title: "Authority & Citation Expansion",
  shortTitle: "Citations",
  description: "For local, national, and international targets, I establish accurate citations and build genuine authoritative backlinks to strengthen domain trust and local relevance.",
  deliverables: "Cleaned NAP Citations & High-Trust Link Strategy",
  iconKey: "citations"
}, {
  number: "04",
  title: "Reputation & Review Framework",
  shortTitle: "Reviews",
  description: "I implement structured systems to encourage authentic customer feedback and reviews, reinforcing key trust signals across Search and Google Business Profile.",
  deliverables: "Review Automation Funnel & Trust Profile Hardening",
  iconKey: "reviews"
}, {
  number: "05",
  title: "Structured Data & AI Search Schema",
  shortTitle: "Schema",
  description: "I construct advanced JSON-LD schema markup so Google and modern AI search engines fully comprehend your brand entity, service offerings, and local authority.",
  deliverables: "Custom Rich Snippet Schema & AI Entity Association",
  iconKey: "schema"
}, {
  number: "06",
  title: "Transparent Reporting & ROI Tracking",
  shortTitle: "Reporting",
  description: "You receive clear, jargon-free monthly video walk-throughs and dashboards tracking keyword positions, qualified search traffic, leads, and direct business ROI.",
  deliverables: "Custom Analytics Dashboard & Monthly Direct Strategy Call",
  iconKey: "reporting"
}];
function getStepIcon(key: string) {
  switch (key) {
    case "audit":
      return <FileSearch className="size-6 text-primary-foreground" />;
    case "optimization":
      return <Wrench className="size-6 text-primary-foreground" />;
    case "citations":
      return <MapPin className="size-6 text-primary-foreground" />;
    case "reviews":
      return <Star className="size-6 text-primary-foreground" />;
    case "schema":
      return <Code2 className="size-6 text-primary-foreground" />;
    case "reporting":
      return <BarChart3 className="size-6 text-primary-foreground" />;
    default:
      return <CheckCircle2 className="size-6 text-primary-foreground" />;
  }
}
export default function Methodology() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".methodology-card", {
        y: 35
      });
      gsap.to(".methodology-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".methodology-card", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="methodologyprocess" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground overflow-hidden">
      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground px-3 py-1 font-medium rounded-full">
            Direct Consultant Process
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif">
            My Proven 6-Step SEO Methodology
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            No middle managers or junior account handlers. When you partner with
            YE Consultancy, I personally direct and execute each stage of your
            tailored search campaign.
          </p>

          <div className="mt-6 inline-flex items-center gap-2 rounded-lg bg-muted px-4 py-2 text-xs sm:text-sm font-medium text-foreground">
            <UserCheck className="size-4 text-primary shrink-0" />
            <span>You work directly with Yassine Essaadi on every deliverable</span>
          </div>
        </div>

        {}
        <div className="relative">
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-accent/30 -translate-y-1/2 z-0 pointer-events-none" aria-hidden="true" />

          {}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 relative z-10">
            {stepsData.map((step, index) => <Card key={step.number} data-index={index} className="methodology-card flex flex-col justify-between rounded-2xl bg-card border border-border shadow-md hover:shadow-lg transition-all duration-300 relative group overflow-hidden" style={{
            opacity: 0
          }}>
                {}
                <div className="h-1.5 w-full bg-primary group-hover:bg-accent transition-colors duration-300" />

                <CardHeader className="pt-6 pb-2">
                  <div className="flex items-center justify-between mb-4">
                    {}
                    <div className="flex items-center justify-center size-12 rounded-xl bg-primary text-primary-foreground font-bold text-lg shadow-sm">
                      {step.number}
                    </div>

                    {}
                    <span className="text-xs font-semibold uppercase tracking-wider text-accent-foreground bg-accent/20 border border-accent/40 rounded-full px-3 py-1">
                      {step.shortTitle}
                    </span>
                  </div>

                  <CardTitle className="text-xl font-bold text-card-foreground font-serif leading-snug group-hover:text-primary transition-colors">
                    {step.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4 pb-6 flex-grow">
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </CardDescription>

                  <div className="pt-3 border-t border-border/60">
                    <span className="text-xs font-semibold uppercase tracking-wider text-foreground block mb-1">
                      Key Deliverables:
                    </span>
                    <p className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                      <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                      <span>{step.deliverables}</span>
                    </p>
                  </div>
                </CardContent>

                {}
                <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-primary via-accent to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Card>)}
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-20 p-8 md:p-10 rounded-2xl bg-card border border-border shadow-lg text-center max-w-4xl mx-auto relative overflow-hidden">
          <div className="relative z-10 space-y-6">
            <h3 className="text-2xl sm:text-3xl font-bold text-card-foreground font-serif">
              Ready to execute a structured search strategy?
            </h3>
            <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
              Book a direct consultation with me today. We will review your current website performance and map out a practical action plan tailored to your business goals.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium rounded-xl shadow-md">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium rounded-xl border-border hover:border-primary">
                <Link to="/CaseStudiesPage">
                  <span>Explore My Case Studies</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}